/**
 * WikiRenderer — Wiki 게시판 본문(우측) V3 Hybrid 레이아웃
 *
 * 핸드오프 v2(list) 적용:
 * - 메인 헤더: 타이틀 + 카운트 + 정렬 dropdown + 검색
 * - 핀 섹션: amber tinted 카드 grid (1~3열 반응형, 핀 0개일 때 통째로 숨김)
 * - 일반 섹션: list rows + plain_text 1줄 미리보기 + 우측 메타
 * - 좌측 페이지 트리(WikiSidebar)는 BoardPage 레벨에서 별도 mount.
 */
import React, { useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { cn } from '../../../lib/utils';
import {
  FileText, Pin, ArrowUpDown, ChevronDown, Check,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../../components/ui/dropdown-menu';
import { Post } from '../../../models/Board';
import { StyleRendererProps, timeAgo } from './shared';
import { UserAvatar } from '../../components/UserAvatar';
import { useResponsive } from '../../../hooks/useResponsive';

type SortKey = 'recent' | 'created';

function useSortLabels(): Record<SortKey, string> {
  return {
    recent: t`Recently edited`,
    created: t`Recently created`,
  };
}

/** plain_text 1줄 미리보기 — null 안전 + truncate는 CSS에 위임 */
function previewText(post: Post): string {
  const raw = (post.plain_text ?? '').trim();
  if (!raw) return '';
  return raw.replace(/\s+/g, ' ');
}

export const WikiRenderer: React.FC<StyleRendererProps> = ({
  posts, onPostClick, selectedPostId,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const sortLabels = useSortLabels();
  // 컨테이너 폭 기준 반응형 (채널 분할 패널은 RoomPage 가 ResponsiveOverrideContext 로 패널 폭 주입)
  const { isMobile, isSidebarCollapsed } = useResponsive();

  /** 핀 / 일반 분리 + 정렬 — 검색은 상단 필터바(PostListView) 의 서버 검색으로 통합 */
  const { pinned, regular } = useMemo(() => {
    const pinnedList = posts.filter(p => p.is_pinned);
    const regularList = posts.filter(p => !p.is_pinned);

    const cmp = sortKey === 'created'
      ? (a: Post, b: Post) => b.created_at.localeCompare(a.created_at)
      : (a: Post, b: Post) => {
          const aT = a.updated_at ?? a.created_at;
          const bT = b.updated_at ?? b.created_at;
          return bT.localeCompare(aT);
        };

    pinnedList.sort(cmp);
    regularList.sort(cmp);
    return { pinned: pinnedList, regular: regularList };
  }, [posts, sortKey]);

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full bg-background">
      {/* ── 메인 헤더 ── */}
      <div className="flex items-center gap-3 px-4 lg:px-5 py-3 border-b border-border shrink-0">
        <span className="text-body font-semibold text-foreground"><Trans>All pages</Trans></span>
        <span className="text-meta text-muted-foreground tabular-nums">
          {posts.length}
        </span>
        <div className="flex-1" />

        {/* 정렬 dropdown — sm 이상에서만 노출 (h≈24, py 1px, px 8px) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="hidden sm:inline-flex items-center gap-1 py-1 px-2 rounded-control text-meta text-foreground border border-border hover:bg-[rgb(var(--surface-hover))] transition-colors"
              title={t`Sort`}
            >
              <ArrowUpDown className="size-3" />
              <span>{sortLabels[sortKey]}</span>
              <ChevronDown className="size-3 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4}>
            {(['recent', 'created'] as SortKey[]).map(k => (
              <DropdownMenuItem
                key={k}
                onClick={() => setSortKey(k)}
                className={cn(
                  'gap-2 text-meta',
                  sortKey === k && 'font-semibold text-primary',
                )}
              >
                <span className="flex-1">{sortLabels[k]}</span>
                {sortKey === k && <Check className="size-3 text-primary" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

      </div>

      {/* ── 본문 ── */}
      <div className="flex-1 overflow-y-auto px-4 lg:px-5 py-4 space-y-5">
        {posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <FileText className="size-10 mb-3 opacity-40" />
            <p className="text-sm"><Trans>No pages yet</Trans></p>
            <p className="text-xs mt-1 opacity-70"><Trans>Create the first page from "Write" at the top</Trans></p>
          </div>
        ) : (
          <>
            {/* 핀 섹션 (핀 0개면 통째로 숨김) */}
            {pinned.length > 0 && (
              <section>
                <div className="flex items-center gap-1.5 mb-2 text-section-head text-badge-caution">
                  <Pin className="size-3 fill-current" />
                  <Trans>Pinned</Trans> ({pinned.length})
                </div>
                <div className={cn('grid gap-3', isMobile ? 'grid-cols-1' : isSidebarCollapsed ? 'grid-cols-2' : 'grid-cols-3')}>
                  {pinned.map(p => (
                    <PinnedCard
                      key={p.id}
                      post={p}
                      selected={selectedPostId === p.id}
                      onClick={() => onPostClick(p)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* 일반 리스트 — 섹션 헤더는 상단 메인 헤더("All pages")와 중복이라 생략 */}
            {regular.length > 0 && (
              <section>
                <ListRows
                  posts={regular}
                  onPostClick={onPostClick}
                  selectedPostId={selectedPostId}
                />
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/** Pinned amber tinted card */
const PinnedCard: React.FC<{
  post: Post;
  selected: boolean;
  onClick: () => void;
}> = ({ post, selected, onClick }) => {
  const preview = previewText(post);
  return (
    <button
      type="button"
      onClick={onClick}
      data-state={selected ? 'on' : undefined}
      className={cn(
        'group text-left rounded-card p-3 border transition-colors',
        // 핸드오프 spec: bg = caution 5% + card 95%, border = caution 22% + border 78%
        'bg-[color-mix(in_srgb,rgb(var(--badge-caution))_5%,rgb(var(--card)))]',
        'border-[color-mix(in_srgb,rgb(var(--badge-caution))_22%,rgb(var(--border)))]',
        'hover:border-[color-mix(in_srgb,rgb(var(--badge-caution))_50%,rgb(var(--border)))] hover:shadow-caution-md',
        'data-[state=on]:border-badge-caution',
      )}
    >
      <div className="text-section-head text-badge-caution inline-flex items-center gap-1.5 mb-1.5">
        <Pin className="size-2.5 fill-current" />
        <Trans>Pinned</Trans>
      </div>
      <h3 className="text-label font-semibold text-foreground leading-snug tracking-[-0.005em] line-clamp-2 mb-1">
        {post.title || t`Untitled`}
      </h3>
      {preview && (
        <p className="text-meta text-muted-foreground leading-relaxed line-clamp-2 mb-2">
          {preview}
        </p>
      )}
      <div className="flex items-center gap-1.5 text-meta-sm text-muted-foreground">
        <UserAvatar src={post.author.photoUrl ?? null} name={post.author.name} className="size-[18px]" />
        <span className="truncate max-w-[100px]">{post.author.name}</span>
        <span className="opacity-50">·</span>
        <span className="whitespace-nowrap">{timeAgo(post.updated_at ?? post.created_at)}</span>
      </div>
    </button>
  );
};

/** Regular list rows */
const ListRows: React.FC<{
  posts: Post[];
  onPostClick: (post: Post) => void;
  selectedPostId?: string | null;
  /** 핀 우선 모드에서 핀 행에 amber 배경 강조 */
  withPinHighlight?: boolean;
}> = ({ posts, onPostClick, selectedPostId, withPinHighlight }) => (
  <div className="flex flex-col">
    {posts.map(p => {
      const preview = previewText(p);
      const isPinHighlight = withPinHighlight && p.is_pinned;
      return (
        <button
          key={p.id}
          type="button"
          onClick={() => onPostClick(p)}
          data-state={selectedPostId === p.id ? 'on' : undefined}
          className={cn(
            'group w-full text-left flex items-start gap-4 px-3 py-3 border-b border-border/50 transition-colors',
            'hover:bg-muted/50',
            'data-[state=on]:bg-accent',
            isPinHighlight && 'bg-badge-caution-bg',
          )}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <h4 className="text-body font-semibold text-foreground truncate tracking-[-0.005em]">
                {p.title || t`Untitled`}
              </h4>
              {p.is_pinned && !withPinHighlight && (
                <Pin className="shrink-0 size-3 text-badge-caution fill-current" />
              )}
            </div>
            {preview && (
              <p className="text-meta text-muted-foreground leading-normal line-clamp-1 mt-1">
                {preview}
              </p>
            )}
          </div>
          <div className="hidden sm:flex flex-col items-end gap-1 shrink-0 text-meta-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <UserAvatar src={p.author.photoUrl ?? null} name={p.author.name} className="size-[18px]" />
              <span className="truncate max-w-[100px]">{p.author.name}</span>
            </span>
            <span className="whitespace-nowrap">{timeAgo(p.updated_at ?? p.created_at)}</span>
          </div>
        </button>
      );
    })}
  </div>
);
